import { useEffect, useState } from "react";
import {
  AppBorderBox,
  AppBox,
  AppButton,
  AppModal,
  AppPaper,
  AppSkeletonGlobalCardList,
  AppText,
  AppTitle,
} from "../../../../../commons/components";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { FootballTeamCoach } from "../../../../../commons/models/football/football-team-coach";
import UserSearch from "../../../users/components/UserSearch";
import { IFootballTeamCoach } from "../../../../../commons/models/football/interface/i-football-team-coach";
import IconPersonDelete from "../../../../../commons/components/icons/person-delete";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import { Can } from "../../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import IconPersonAdd from "../../../../../commons/components/icons/person-add";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import { User } from "../../../../../commons/models/user";
import SubTeamCoachAddConfirm from "./SubTeamCoachAddConfirmation";
import SubTeamCoachRemove from "../../../teams/components/teamCoach/TeamCoachRemove";
import SubTeamCoachRemovalInprogress from "../../../teams/components/teamCoach/TeamCoachRemoveInprogress";
import { ManageFootballTeam } from "../../../../../commons/models/football/admin/manage-football-team";
import {
  fetchManageSubTeamCoachList,
  IManageSubTeamCoachesOption,
} from "../../../../../redux/slices/football/admin/team/sub-team/coach/manage-sub-team-coaches-slice";
import { subTeamCoachAdd } from "../../../../../redux/slices/football/admin/team/sub-team/coach/manage-sub-team-coach-add-slice";
import { subTeamCoachRemove } from "../../../../../redux/slices/football/admin/team/sub-team/coach/manage-sub-team-coach-remove-slice";
import SubTeamCoachCard from "./SubTeamCoachCard";

export default function SubTeamCoach() {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { subTeamInfo } = useAppSelector((state) => state.footballManageSubTeam);
  const { manageSubTeamCoachsList, manageSubTeamCoachsListStatus } = useAppSelector(
    (state) => state.footballManageSubTeamCoaches
  );
  const { manageSubTeamCoachRemoveStatus } = useAppSelector((state) => state.footballManageSubTeamCoachRemove);
  const [isCoachAdd, setIsCoachAdd] = useState(false);
  const [selectedSubTeamCoach, setSelectedSubTeamCoach] = useState<FootballTeamCoach | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isRemoveSubTeamCoach, setRemoveSubTeamCoach] = useState(false);

  useEffect(() => {
    if (subTeamInfo.id) {
      const coachListOption: IManageSubTeamCoachesOption = {
        teamId: subTeamInfo.parent.id,
        subTeamId: subTeamInfo.id,
      };
      dispatch(fetchManageSubTeamCoachList(coachListOption));
    }
  }, [subTeamInfo]);

  const handleCoachAdd = () => {
    if (selectedUser) {
      dispatch(
        subTeamCoachAdd({
          teamId: subTeamInfo.parent.id,
          subTeamId: subTeamInfo.id,
          userId: selectedUser.id,
        })
      )
        .unwrap()
        .then(() => {
          setSelectedSubTeamCoach(null);
          addSnackbar({
            key: "sub-coach-add",
            text: "Coach Added Successfully",
            variant: "success",
          });
          dispatch(
            fetchManageSubTeamCoachList({
              teamId: subTeamInfo.parent.id,
              subTeamId: subTeamInfo.id,
            })
          );
          setSelectedSubTeamCoach(null);
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
    setSelectedUser(null);
  };

  const handleCoachRemoveConfirm = () => {
    if (selectedSubTeamCoach) {
      dispatch(
        subTeamCoachRemove({
          teamId: subTeamInfo.parent.id,
          subTeamId: subTeamInfo.id,
          coachId: selectedSubTeamCoach.id,
        })
      )
        .unwrap()
        .then(() => {
          setSelectedSubTeamCoach(null);
          addSnackbar({
            key: "sub-coach-remove",
            text: "Coach Removed Successfully",
            variant: "success",
          });
          dispatch(
            fetchManageSubTeamCoachList({
              teamId: subTeamInfo.parent.id,
              subTeamId: subTeamInfo.id,
            })
          );
          setSelectedSubTeamCoach(null);
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
  };

  return (
    <>
      <AppBox gap="sm" flexDirection="column">
        <AppBox alignItems="center" justifyContent="space-between">
          <AppTitle as="h5">Coach</AppTitle>
        </AppBox>
        {manageSubTeamCoachsList.length === 0 ? (
          <AppPaper padding="sm" shadow="xs">
            <AppBorderBox borderVariant="dashed">
              <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center" p="sm">
                <AppBox alignItems="center" justifyContent="center" gap="xs">
                  <Can I={RolePolicyActionEnum.UPDATE} this={new ManageFootballTeam(subTeamInfo.parent)}>
                    <AppBox>
                      <IconPersonAdd width={40} height={40} color="rgba(var(--border-200))" />
                    </AppBox>
                  </Can>
                  <AppText fontWeight="semibold">There is not any coach in the sub-team.</AppText>
                </AppBox>
                <Can I={RolePolicyActionEnum.UPDATE} this={new ManageFootballTeam(subTeamInfo.parent)}>
                  <AppButton size="sm" label="Add Coach" onClick={() => setIsCoachAdd(true)} />
                </Can>
              </AppBox>
            </AppBorderBox>
          </AppPaper>
        ) : (
          <>
            {manageSubTeamCoachsListStatus === StatusEnum.Loading ? (
              <>
                <AppSkeletonGlobalCardList numberOfItems={2} withAvatar noTitle />
              </>
            ) : (
              <AppPaper padding="none" shadow="xs">
                <AppBox flexDirection="column">
                  {manageSubTeamCoachsList.all().map((coach, index) => (
                    <AppBorderBox
                      key={index}
                      className="w-100"
                      border={
                        manageSubTeamCoachsList.length === 1
                          ? []
                          : index === 0
                          ? ["Bottom"]
                          : index === manageSubTeamCoachsList.length - 1
                          ? ["Top"]
                          : ["Top", "Bottom"]
                      }
                    >
                      <SubTeamCoachCard
                        coachInfo={coach}
                        onCoachRemove={() => {
                          setSelectedSubTeamCoach(coach);
                          setRemoveSubTeamCoach(true);
                        }}
                      />
                    </AppBorderBox>
                  ))}
                </AppBox>
              </AppPaper>
            )}
          </>
        )}
      </AppBox>
      <AppModal
        opened={isCoachAdd}
        onClose={() => {
          setIsCoachAdd(false);
        }}
        titleIcon={<IconPersonAdd />}
        title={"Choose Coach For Sub-Team"}
        withoutButtons
      >
        <UserSearch
          userSelectedUsers={manageSubTeamCoachsList.length > 0 ? (manageSubTeamCoachsList.all() as User[]) : []}
          onSelectedUsers={(users) => {
            setIsCoachAdd(false);
            setSelectedUser(users[0]);
          }}
          teamAvailableUsers
          team={new ManageFootballTeam(subTeamInfo.parent)}
        />
      </AppModal>
      {selectedUser && (
        <AppModal
          opened={selectedUser !== null}
          onClose={() => {
            setSelectedUser(null);
          }}
          titleIcon={<IconPersonAdd />}
          title={"Add Coach To Sub-team"}
          onSave={() => {
            handleCoachAdd();
          }}
        >
          <SubTeamCoachAddConfirm user={selectedUser} />
        </AppModal>
      )}
      <AppModal
        opened={isRemoveSubTeamCoach}
        onClose={() => {
          setRemoveSubTeamCoach(false);
          setSelectedSubTeamCoach(null);
        }}
        title="Want To Remove This Coach?"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        confirmButtonLabel="Remove"
        onSave={() => {
          setRemoveSubTeamCoach(false);
          handleCoachRemoveConfirm();
        }}
      >
        <SubTeamCoachRemove teamCoach={selectedSubTeamCoach as IFootballTeamCoach} />
      </AppModal>
      <AppModal
        opened={manageSubTeamCoachRemoveStatus === StatusEnum.Loading}
        title="Removing Coach"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        withoutCancelButton
        withoutButtons
        withoutCloseButton
      >
        <SubTeamCoachRemovalInprogress teamCoach={selectedSubTeamCoach as IFootballTeamCoach} />
      </AppModal>
    </>
  );
}
